import IconButton from '@mui/material/IconButton';
import '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api/ApplicationApi';
import { Trash } from 'assets/volvoIcons';
import { AreaName } from 'domain/constants';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import { Sxable } from 'library/types';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';

export type FileRemoveButtonProps = {
	fileSetId: number;
	fileId: number;
	onDelete: (isSubmitting: boolean) => void;
};

export interface DeleteRefreshFunction {
	onDelete: (isSubmitting: boolean) => void;
}

export const UnitFileRemoveButton = ({
	fileSetId,
	fileId,
	onDelete,
	sx = [],
}: Sxable &
	DeleteRefreshFunction &
	FileRemoveButtonProps): React.JSX.Element => {
	const { t } = useTranslation(['commonButton', 'common']);
	const { open, handleOpen, handleClose } = useDialogState();
	const { enqueueSnackbar } = useSnackbar();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();
	const { api } = useProductCenterApi();
	const [isDeleting, setIsDeleting] = useState(false);

	const unitIdParsed = useResolvedUnitIds().id;

	const handleOnAccept = useCallback(() => {
		setIsDeleting(true);
		api.removeFile({ fileSetId, fileId, unitId: unitIdParsed })
			.then(() => {
				enqueueSnackbar(t('common:file-remove-success'), {
					variant: 'success',
				});
				onDelete(true);
			})
			.catch((error) => {
				if (checkConsoleDebuggingReadAccess()) {
					console.error(`Error while removing file: ${error}`);
				}
				enqueueSnackbar((error as Error)?.message, {
					variant: 'error',
					style: { whiteSpace: 'pre-line' },
				});
			})
			.finally(() => {
				setIsDeleting(false);
				handleClose();
			});
	}, [
		t,
		api,
		enqueueSnackbar,
		fileSetId,
		fileId,
		checkConsoleDebuggingReadAccess,
		handleClose,
		onDelete,
		unitIdParsed,
	]);

	return (
		<AuthorizeComponent
			areaName={AreaName.RemoveFile}
			permission={OperationType.Read}>
			<IconButton onClick={handleOpen} sx={sx}>
				<Trash />
			</IconButton>
			{open && (
				<CustomSeverityDialog
					acceptButtonLabel={t('commonButton:yes')}
					declineButtonLabel={t('commonButton:no')}
					content={t('common:file-remove-confirmation')}
					headerText={t('commonButton:delete')}
					isOpen
					onAccept={handleOnAccept}
					onDecline={handleClose}
					disabledAcceptButton={isDeleting}
				/>
			)}
		</AuthorizeComponent>
	);
};

UnitFileRemoveButton.displayName = 'UnitFileRemoveButton';
